import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { EditDataType } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { SystemConfigItemType, SystemConfigStructureItemType } from 'src/types/systemConfigDetail';
import TreeBlock from './TreeBlock';
import StructureItemBlock from './StructureItemBlock';
import { addMessage } from 'src/resources/alertMessages/reducer';
import {
    clearSystemConfigDetail,
    getSystemConfigDetailFetch,
    updateSystemConfigDetailFetch,
    getSystemConfigStructureFetch,
    createSystemConfigStructureFetch,
    updateSystemConfigStructureFetch,
} from 'src/resources/systemConfigDetail/reducer';

export type OnChangeType = (
    structureItem: SystemConfigStructureItemType,
    value: string | number | undefined,
    error: boolean
) => void;

export const EDIT_KEYS = ['USER_NOTIFICATIONS'];
export const EDIT_STRUCTURE_KEYS = ['ITEM_KEY', 'TYPE', 'DESCRIPTION'];

export const ID_KEY = 'ITEM_KEY';

const initData = {};

const SystemConfigDetailContainer: FC = () => {
    const dispatch = useDispatch();

    const data = useSelector((state: RootStoreType) => state.systemConfigDetail.data);
    const structureData = useSelector((state: RootStoreType) => state.systemConfigDetail.structureData);
    const loading = useSelector((state: RootStoreType) => state.systemConfigDetail.loading);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes ?? []);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes ?? []);
    const configItemTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.configItemTypeCodes);

    const [editData, setEditData] = useState<EditDataType>(initData);
    const [editStructureData, setEditStructureData] = useState<SystemConfigStructureItemType | undefined>(undefined);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionUpdate = useCheckPermission('SYSTEM_CONFIG.UPDATE');
    const permissionUpdateStructure = useCheckPermission('SYSTEM_CONFIG_STRUCTURE.UPDATE');

    const backUrl = '/admin/system/';

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('systemConfigDetail.title'), backUrl }));
        dispatch(getSystemConfigDetailFetch());
        return () => {
            clearSystemConfigDetail();
        };
    }, []);

    useEffect(() => {
        if (!structureData) {
            dispatch(getSystemConfigStructureFetch());
        }
    }, [structureData]);

    useEffect(() => {
        if (data) {
            const newEditData: EditDataType = {};
            data.forEach((item) => {
                newEditData[item[ID_KEY]] = item.VALUE;
            });
            setEditData(newEditData);
        }
    }, [data]);

    const onChange: OnChangeType = (structureItem, value) => {
        const itemKey = structureItem[ID_KEY];
        if (!itemKey) {
            return;
        }
        const newData = { ...editData };
        if (value === undefined || value === null) {
            delete newData[itemKey];
        } else {
            newData[itemKey] = String(value);
        }
        setEditData(newData);
    };

    const handleSave = () => {
        if (structureData) {
            const newData: SystemConfigItemType[] = structureData
                .filter((item) => item[ID_KEY] && editData[item[ID_KEY]] !== undefined)
                .map((item) => ({
                    ITEM_KEY: item[ID_KEY] || '',
                    VALUE: String(editData[item[ID_KEY] as string] ?? ''),
                }));
            dispatch(updateSystemConfigDetailFetch(newData));
        }
    };
    const handleSaveStructure = (structureItem: EditDataType) => {
        if (structureItem.ID) {
            dispatch(
                updateSystemConfigStructureFetch({
                    id: structureItem.ID,
                    data: structureItem,
                })
            );
        }
        if (!structureData?.find((structureDataItem) => structureDataItem[ID_KEY] === structureItem[ID_KEY])) {
            dispatch(createSystemConfigStructureFetch(structureItem));
        } else {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('systemConfig.messages.keyAlreadyUsed'),
                })
            );
        }
    };

    const handleSetEditStructure = (structureItem?: SystemConfigStructureItemType) => {
        setEditStructureData(structureItem);
    };

    const handleAddMessage = (message: any) => {
        dispatch(addMessage(message));
    };

    return (
        <>
            <TopBlock
                permissionUpdate={permissionUpdate}
                permissionUpdateStructure={permissionUpdateStructure}
                handleSave={handleSave}
                editStructureData={editStructureData}
                handleNewStructureData={() => handleSetEditStructure()}
            />

            {loading && <Loading />}

            <StructureItemBlock
                editData={editStructureData}
                permissionUpdateStructure={permissionUpdateStructure}
                configItemTypeCodes={configItemTypeCodes}
                handleSubmit={handleSaveStructure}
                handleAddMessage={handleAddMessage}
            />

            <TreeBlock
                structureData={structureData}
                onChange={onChange}
                boolNumberCodes={boolNumberCodes}
                serviceCodes={serviceCodes}
                editData={editData}
                loading={!!loading}
                permissionUpdate={permissionUpdate}
                setEditStructure={handleSetEditStructure}
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default SystemConfigDetailContainer;
